import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { calculateSplitTotal, type SplitItem as SplitCalcItem } from "../utils/splitCalc.ts";
import { getHydratedSplitAmountState, useSplitAmount } from "./useSplitAmount.ts";
import { useSplitDiscounts } from "./useSplitDiscounts.ts";
import { reconcileSplitItems, useSplitItems } from "./useSplitItems.ts";
import { ORDER_TYPE } from "../utils/constants.ts";
import type {
  CheckoutMode,
  SplitTotal,
  SplitType,
  UseSplitPaymentProps,
  UseSplitPaymentReturn,
} from "./types.ts";

const generateSplitUniquePaymentId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `split-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const useSplitPayment = ({
  tableId,
  orderType,
  tableDetail,
  availableItems,
  orderPriceDetail,
  giftCardApplied,
  couponCodeApplied,
  clearAllTogetherDiscounts,
  restSettingDetails,
  SWAL_SETTINGS,
  isOrderValid,
}: UseSplitPaymentProps): UseSplitPaymentReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasSplitPayment, setHasSplitPayment] = useState<boolean>(false);
  const [activeSplitType, setActiveSplitType] = useState<SplitType | null>(null);
  const [splitMode, setSplitMode] = useState<SplitType>("items");
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>("all_together");
  const [isPreparingSplitPayment, setIsPreparingSplitPayment] = useState<boolean>(false);

  const activeOrderKeyRef = useRef<string | null>(null);

  const getPayableTotal = useCallback(
    (): number =>
      Number(
        giftCardApplied || couponCodeApplied
          ? orderPriceDetail?.amount_after_discount
          : orderPriceDetail?.great_grand_total,
      ) || 0,
    [giftCardApplied, couponCodeApplied, orderPriceDetail],
  );

  const getRemainingAmount = useCallback(
    (): number =>
      Number(hasSplitPayment ? tableDetail?.activeOrder?.remaining_amount : getPayableTotal()) || 0,
    [hasSplitPayment, tableDetail?.activeOrder?.remaining_amount, getPayableTotal],
  );

  const lockedSplitMode: SplitType = useMemo(
    () => hasSplitPayment && activeSplitType ? activeSplitType : splitMode,
    [hasSplitPayment, activeSplitType, splitMode],
  );

  const isSplitCheckout: boolean = checkoutMode === "split" || hasSplitPayment;

  const {
    splitItems,
    setSplitItems,
    normalizedSplitItems,
    handleSplitItemToggle,
    handleSplitQuantityChange,
    handleSelectAllSplitItems,
    handleDeselectAllSplitItems,
  } = useSplitItems({ availableItems });

  const {
    splitAmountMode,
    setSplitAmountMode,
    splitAmountInput,
    setSplitAmountInput,
    splitPeopleCount,
    setSplitPeopleCount,
    amountSplitPreview,
    splitAmountWarning,
    isWholeNumberValue,
  } = useSplitAmount({ getRemainingAmount });

  useEffect(() => {
    const splitType = (tableDetail?.activeOrder?.split_type as SplitType | null | undefined) ?? null;
    const hasSplit = !!tableDetail?.activeOrder?.has_split_payment;
    const activeOrderKey = tableDetail?.activeOrder?._id
      ? String(tableDetail.activeOrder._id)
      : null;
    const isSameActiveOrder = !!activeOrderKey && activeOrderKeyRef.current === activeOrderKey;

    activeOrderKeyRef.current = activeOrderKey;

    setHasSplitPayment(hasSplit);
    setActiveSplitType(splitType);
    setSplitMode((prevMode) => splitType ?? (isSameActiveOrder ? prevMode : "items"));
    setCheckoutMode((prevMode) => {
      if (hasSplit) return "split";
      if (isSameActiveOrder) return prevMode;
      return "all_together";
    });

    setSplitItems((prevSplitItems) =>
      reconcileSplitItems({
        availableItems,
        hasSplit,
        splitType,
        isSameActiveOrder,
        previousSplitItems: prevSplitItems,
      })
    );

    if (splitType === "amount") {
      const { nextSplitAmountMode, nextSplitAmount, nextSplitPeopleCount } =
        getHydratedSplitAmountState({
          splitAmountMode: tableDetail?.activeOrder?.split_amount_mode,
          splitAmount: tableDetail?.activeOrder?.split_amount,
          remainingAmount: tableDetail?.activeOrder?.remaining_amount,
          splitPeopleCount: tableDetail?.activeOrder?.split_people_count,
        });

      setSplitAmountMode(nextSplitAmountMode);
      setSplitAmountInput(nextSplitAmount);
      setSplitPeopleCount(nextSplitPeopleCount);
    } else if (!isSameActiveOrder) {
      setSplitAmountMode("manual");
      setSplitAmountInput("");
      setSplitPeopleCount("2");
    }

    localStorage.setItem("has_split_payment", String(hasSplit));
  }, [
    availableItems,
    setSplitAmountInput,
    setSplitAmountMode,
    setSplitItems,
    setSplitPeopleCount,
    tableDetail,
  ]);

  const splitTotal: SplitTotal | null = useMemo(
    () =>
      lockedSplitMode !== "items" || !splitItems.length
        ? null
        : calculateSplitTotal({
          splitItems: splitItems as SplitCalcItem[],
          orderPriceDetail: orderPriceDetail ?? undefined,
          couponCodeApplied: Boolean(couponCodeApplied),
          restSettingDetails,
        }),
    [lockedSplitMode, splitItems, orderPriceDetail, couponCodeApplied, restSettingDetails],
  );

  const baseCurrentSplitTotal: number =
    lockedSplitMode === "amount" ? amountSplitPreview : splitTotal?.great_grand_total || 0;

  const isAmountSplitInvalid: boolean =
    lockedSplitMode === "amount" &&
    (!amountSplitPreview ||
      amountSplitPreview <= 0 ||
      amountSplitPreview > getRemainingAmount() ||
      (splitAmountMode === "equal" && Math.max(1, Number(splitPeopleCount) || 1) < 2));

  const isSplitDisabled: boolean = orderType === ORDER_TYPE.EDIT_SALES;

  const isSplitPayDisabled: boolean =
    !isOrderValid ||
    isPreparingSplitPayment ||
    isSplitDisabled ||
    (lockedSplitMode === "items" ? splitItems.length === 0 : isAmountSplitInvalid);

  const isItemsModeDisabled: boolean = hasSplitPayment && activeSplitType === "amount";
  const isAmountModeDisabled: boolean = hasSplitPayment && activeSplitType === "items";
  const canSwitchToSplitMode: boolean = hasSplitPayment || (!couponCodeApplied && !giftCardApplied);

  const splitDiscountState = useSplitDiscounts({
    tableId,
    tableDetail,
    lockedSplitMode,
    amountSplitPreview,
    splitAmountMode,
    splitPeopleCount,
    normalizedSplitItems,
    SWAL_SETTINGS,
    baseCurrentSplitTotal,
    hasSplitPayment,
    activeOrderKey: activeOrderKeyRef.current,
  });

  const {
    splitPreviewDetail,
    isSplitCouponApplied,
    isSplitGiftCardApplied,
    normalizedSplitCouponCode,
    normalizedSplitGiftCardAmount,
    hasValidSplitGiftCardAmount,
    isCouponInputEnabled,
    isGiftCardInputEnabled,
    clearSplitDiscountWarning,
    previewCurrentSelection,
  } = splitDiscountState;

  const currentSplitTotal: number =
    Number(splitPreviewDetail?.amount_after_discount ?? baseCurrentSplitTotal) || 0;

  const remainingAfterSplit: number =
    Number(
      splitPreviewDetail?.remaining_after ??
      Math.max(0, Math.round(getRemainingAmount() - baseCurrentSplitTotal)),
    ) || 0;

  const wrappedHandleSplitItemToggle = useCallback(
    (item: typeof availableItems[number]): void => {
      clearSplitDiscountWarning();
      handleSplitItemToggle(item);
    },
    [clearSplitDiscountWarning, handleSplitItemToggle],
  );

  const wrappedHandleSplitQuantityChange = useCallback(
    (item: typeof availableItems[number], change: number): void => {
      clearSplitDiscountWarning();
      handleSplitQuantityChange(item, change);
    },
    [clearSplitDiscountWarning, handleSplitQuantityChange],
  );

  const wrappedHandleSelectAllSplitItems = useCallback((): void => {
    clearSplitDiscountWarning();
    handleSelectAllSplitItems();
  }, [clearSplitDiscountWarning, handleSelectAllSplitItems]);

  const wrappedHandleDeselectAllSplitItems = useCallback((): void => {
    clearSplitDiscountWarning();
    handleDeselectAllSplitItems();
  }, [clearSplitDiscountWarning, handleDeselectAllSplitItems]);

  const handleProceedToSplitPay = useCallback(async (): Promise<void> => {
    const couponCodeForCheckout = isSplitCouponApplied ? normalizedSplitCouponCode : "";
    const hasValidWholeGiftCardAmount = isSplitGiftCardApplied ? hasValidSplitGiftCardAmount : false;
    const giftCardAmountForCheckout = hasValidWholeGiftCardAmount ? normalizedSplitGiftCardAmount : 0;

    if (isCouponInputEnabled && !normalizedSplitCouponCode) {
      Swal.fire({ icon: "error", text: t("msg_enter_split_coupon_code"), ...SWAL_SETTINGS });
      return;
    }

    if (isGiftCardInputEnabled && !hasValidSplitGiftCardAmount) {
      Swal.fire({ icon: "error", text: t("label_amount_integer_error"), ...SWAL_SETTINGS });
      return;
    }

    if (isGiftCardInputEnabled && normalizedSplitGiftCardAmount <= 0) {
      Swal.fire({ icon: "error", text: t("msg_enter_valid_split_gift_card_amount"), ...SWAL_SETTINGS });
      return;
    }

    const discountOptions = {
      couponCode: couponCodeForCheckout || undefined,
      giftCardAmount: giftCardAmountForCheckout || undefined,
    };

    if (lockedSplitMode === "amount") {
      const remainingAmount = getRemainingAmount();
      const peopleCount = Math.max(1, Number(splitPeopleCount) || 1);
      const splitAmount = amountSplitPreview;

      if (splitAmountMode === "equal" && peopleCount < 2) {
        Swal.fire({ icon: "error", text: t("msg_enter_at_least_two_people"), ...SWAL_SETTINGS });
        return;
      }
      if (!splitAmount || splitAmount <= 0) {
        Swal.fire({ icon: "error", text: t("msg_enter_valid_split_amount"), ...SWAL_SETTINGS });
        return;
      }
      if (splitAmountMode === "manual" && !isWholeNumberValue(splitAmountInput)) return;
      if (splitAmount > remainingAmount) {
        Swal.fire({ icon: "error", text: t("label_split_amount_exceeds_remaining"), ...SWAL_SETTINGS });
        return;
      }

      try {
        setIsPreparingSplitPayment(true);
        const previewData = await previewCurrentSelection({
          couponCode: discountOptions.couponCode,
          giftCardAmount: discountOptions.giftCardAmount,
        });
        if (!previewData) return;

        navigate(`/cashier/payment-via-cash/${tableId}`, {
          state: {
            isSplitPayment: true,
            unique_payment_id: generateSplitUniquePaymentId(),
            splitType: "amount",
            splitAmountMode,
            splitPeopleCount: splitAmountMode === "equal" ? peopleCount : undefined,
            splitAmount,
            splitPriceDetail: previewData?.price_detail,
            splitCouponCode: couponCodeForCheckout || null,
            splitGiftCardAmount: giftCardAmountForCheckout || 0,
            order_id: tableDetail?.activeOrder?._id,
          },
        });
      } finally {
        setIsPreparingSplitPayment(false);
      }
      return;
    }

    if (!splitItems.length) {
      Swal.fire({ icon: "error", text: t("msg_select_at_least_one_item"), ...SWAL_SETTINGS });
      return;
    }

    try {
      setIsPreparingSplitPayment(true);
      const previewData = await previewCurrentSelection({
        couponCode: discountOptions.couponCode,
        giftCardAmount: discountOptions.giftCardAmount,
      });
      if (!previewData) return;

      navigate(`/cashier/payment-via-cash/${tableId}`, {
        state: {
          splitItems: normalizedSplitItems,
          splitPriceDetail: previewData?.price_detail,
          isSplitPayment: true,
          unique_payment_id: generateSplitUniquePaymentId(),
          splitType: "items",
          splitCouponCode: couponCodeForCheckout || null,
          splitGiftCardAmount: giftCardAmountForCheckout || 0,
          order_id: tableDetail?.activeOrder?._id,
        },
      });
    } finally {
      setIsPreparingSplitPayment(false);
    }
  }, [
    isSplitCouponApplied,
    normalizedSplitCouponCode,
    isSplitGiftCardApplied,
    hasValidSplitGiftCardAmount,
    normalizedSplitGiftCardAmount,
    isCouponInputEnabled,
    isGiftCardInputEnabled,
    t,
    SWAL_SETTINGS,
    lockedSplitMode,
    getRemainingAmount,
    splitPeopleCount,
    amountSplitPreview,
    splitAmountMode,
    isWholeNumberValue,
    splitAmountInput,
    previewCurrentSelection,
    navigate,
    tableId,
    tableDetail?.activeOrder?._id,
    splitItems,
    normalizedSplitItems,
  ]);

  const handleCheckoutModeChange = useCallback(async (nextMode: CheckoutMode): Promise<void> => {
    if (nextMode === "split") {
      if (!canSwitchToSplitMode) {
        const clearedDiscounts = await clearAllTogetherDiscounts?.();
        if (!clearedDiscounts) return;
      }
      setCheckoutMode("split");
      return;
    }

    if (hasSplitPayment) {
      Swal.fire({
        icon: "error",
        text: t("msg_split_payment_already_in_progress"),
        ...SWAL_SETTINGS,
      });
      return;
    }

    setCheckoutMode("all_together");
  }, [canSwitchToSplitMode, clearAllTogetherDiscounts, hasSplitPayment, t, SWAL_SETTINGS]);

  const handleSplitModeChange = useCallback((mode: SplitType): void => {
    if (hasSplitPayment && activeSplitType && activeSplitType !== mode) {
      Swal.fire({
        icon: "error",
        text: `This order is already locked to split by ${activeSplitType}.`,
        ...SWAL_SETTINGS,
      });
      return;
    }

    setSplitMode(mode);
  }, [hasSplitPayment, activeSplitType, SWAL_SETTINGS]);

  const remainingAmount = getRemainingAmount();
  const isManualModeDisabled = hasSplitPayment && lockedSplitMode === "amount" && splitAmountMode === "equal";
  const isEqualModeDisabled = hasSplitPayment && lockedSplitMode === "amount" && splitAmountMode === "manual";

  const splitAmountGroup = {
    splitAmountMode,
    splitAmountInput,
    splitPeopleCount,
    splitAmountWarning,
    isManualModeDisabled,
    isEqualModeDisabled,
    amountSplitPreview,
    remainingAmount,
    remainingAfterSplit,
    setSplitAmountMode,
    setSplitAmountInput,
    setSplitPeopleCount,
  };

  const splitItemsGroup = {
    splitItems,
    availableItems,
    handleSplitItemToggle: wrappedHandleSplitItemToggle,
    handleSelectAllSplitItems: wrappedHandleSelectAllSplitItems,
    handleDeselectAllSplitItems: wrappedHandleDeselectAllSplitItems,
    handleSplitQuantityChange: wrappedHandleSplitQuantityChange,
  };

  return {
    // Grouped concerns for components
    splitAmount: splitAmountGroup,
    splitItemsGroup,
    splitDiscounts: {
      splitCouponCode: splitDiscountState.splitCouponCode,
      splitGiftCardAmount: splitDiscountState.splitGiftCardAmount,
      splitPreviewDetail: splitDiscountState.splitPreviewDetail,
      splitDiscountWarning: splitDiscountState.splitDiscountWarning,
      discountsLocked: splitDiscountState.discountsLocked,
      splitDiscountLockedReason: splitDiscountState.splitDiscountLockedReason,
      handleSplitCouponToggle: splitDiscountState.handleSplitCouponToggle,
      handleSplitCouponCodeChange: splitDiscountState.handleSplitCouponCodeChange,
      handleApplySplitCoupon: splitDiscountState.handleApplySplitCoupon,
      handleRemoveSplitCoupon: splitDiscountState.handleRemoveSplitCoupon,
      handleSplitGiftCardToggle: splitDiscountState.handleSplitGiftCardToggle,
      handleSplitGiftCardAmountChange: splitDiscountState.handleSplitGiftCardAmountChange,
      handleApplySplitGiftCard: splitDiscountState.handleApplySplitGiftCard,
      handleRemoveSplitGiftCard: splitDiscountState.handleRemoveSplitGiftCard,
    },
    // Top-level properties not duplicated in any group
    hasSplitPayment,
    activeSplitType,
    splitMode,
    checkoutMode,
    lockedSplitMode,
    isSplitCheckout,
    currentSplitTotal,
    remainingAfterSplit,
    isSplitPayDisabled,
    isItemsModeDisabled,
    isAmountModeDisabled,
    getRemainingAmount,
    handleProceedToSplitPay,
    handleCheckoutModeChange,
    handleSplitModeChange,
  };
};
